import { Box, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { FONT_SIZE, HOLIDAY_GROUPS, INK } from "../../../theme/tokens";
import SystemDialog from "../../UIToolkit/Popups/Dialog";
import { HolidayEffectsList } from "./HolidayEffectsList";
import { HolidaySmsField, NO_SMS, smsMissingText, smsToSend } from "./HolidaySmsField";
import { HolidaySummaryCard } from "./HolidaySummaryCard";
import { type Holiday, type HolidayGroup, HolidayStatus } from "./holidayApi";
import { useDeleteHoliday } from "./holidayQueries";

type Props = { holiday: Holiday; groups: readonly HolidayGroup[]; onClose: () => void };

const APPLIED_EFFECT_KEYS = ["holidays.delete.applied.removeAdded", "holidays.delete.applied.restore", "holidays.delete.applied.notRestored"] as const;

/** "Delete Christmas?" — says what happens to the schedule, which depends on whether it was applied. */
export const HolidayDeleteDialog: FC<Props> = ({ holiday, groups, onClose }) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const remove = useDeleteHoliday();
	const [sms, setSms] = useState(NO_SMS);
	const applied = holiday.status !== HolidayStatus.NotApplied;

	const handleConfirm = () => {
		if (smsMissingText(sms)) {
			showSnackbar(t("holidays.sms.missing"), "warning");
			return;
		}
		remove.mutate(
			{ holidayId: holiday.id, smsMessage: smsToSend(sms) },
			{
				onSuccess: () => {
					showSnackbar(t("holidays.delete.done", { name: holiday.name }), "success");
					onClose();
				},
			},
		);
	};

	const body = (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: { sm: HOLIDAY_GROUPS.DIALOG_BODY_MIN_WIDTH_PX } }}>
			<HolidaySummaryCard holiday={holiday} groups={groups} />
			{applied ? (
				<>
					<HolidayEffectsList heading={t("holidays.delete.whatHeading")} lines={APPLIED_EFFECT_KEYS.map((key) => t(key))} />
					<HolidaySmsField sms={sms} onChange={setSms} label={t("holidays.sms.label")} disabled={remove.isPending} dataTestId="holiday-delete-sms" />
				</>
			) : (
				<Typography sx={{ color: INK.SECONDARY, fontSize: FONT_SIZE.EMPHASIS }}>{t("holidays.delete.notApplied")}</Typography>
			)}
		</Box>
	);

	return (
		<SystemDialog
			open
			onClose={onClose}
			title={t("holidays.delete.title", { name: holiday.name })}
			body={body}
			describeBody
			cancel={t("cancel")}
			confirm={applied ? t("holidays.delete.confirmApplied") : t("delete")}
			confirmButtonVariant="delete"
			onConfirm={handleConfirm}
			confirmLoading={remove.isPending}
			dataTestId="holiday-delete-dialog"
			confirmDataTestId="holiday-delete-confirm"
			cancelDataTestId="holiday-delete-cancel"
		/>
	);
};
